import React from 'react';
import PropTypes from 'prop-types';

interface PaginationProps extends React.HTMLAttributes<HTMLElement> {
    numPerPage?: number;
    data: any[]; // We reference the data so that there's no need for the developer to worry about updating a length state.
    currentPage: number;
    // Baseline arguments
    children?: React.ReactNode;
    className?: string;
    // Functions
    onPageChange: (page: number) => void;
}

/**
 * A list of page options intended to be placed below an itemized list.
 * 
 * ## _
 * ### Arguments
 * @param {number} numPerPage - The amount of items that should be dedicated to a single page. Defaults to 10.
 * @param {array} data - The array of data. The length of it will be used to calculate how many pages there should be, and is not used otherwise.
 * @param {number} currentPage - This should be a state that contains the page that the user is currently on.
 * @param {React.ReactNode} children - UNUSED - The contents inside the pagination row.
 * @param {string} className - CSS classes to apply to the pagination row.
 * 
 * ## _
 * ### Functions
 * @param {function} onPageChange - A function called when one of the pagination items being pressed, passing in the page it should change to. This should usually be a setState for the current page.
 */
class Pagination extends React.Component<PaginationProps> {
    static propTypes = {
        numPerPage: PropTypes.number,
        data: PropTypes.array,
        currentPage: PropTypes.number,
        children: PropTypes.node,
        className: PropTypes.string,
        onPageChange: PropTypes.func,
    }

    static defaultProps = {
        className: "",
    }

    render() {
        const { numPerPage = 10, data, currentPage = 1, onPageChange, children, className, ...rest } = this.props;

        const numPages = Math.ceil(data.length / numPerPage);
        // const numPages = 30;
        const ellipsis = -1;

        const formatPageList = () => {
            // We start with a list of all numbers from 1 to numPages.
            let numList = Array.from({length: numPages }, (_, i) => i + 1);
            
            // If we have less than or equal to 8 pages, we need no changes and can just roll with this.
            if (numPages <= 8) {
                return numList;
            }

            // In other cases, ellipses are needed. These will be based on what the current page is.
            // General rules:
            // Always show the first three and the last two pages.
            // A selected page should always show itself and its neighbors.
            // Connect any gaps using ellipses.

            // If the current page is within the first six pages, show one ellipsis after the first three pages to skip directly to the last two pages.
            if (currentPage <= 6) {
                return [...numList.slice(0, Math.max(3, currentPage + 1)), ellipsis, ...numList.slice(-2)]
            }
            // If the current page is within the last five pages, show one ellipsis before the last two pages to skip directly to the start.
            if (currentPage >= numPages - 4) {
                return [...numList.slice(0, 3), ellipsis, ...numList.slice(Math.min(numPages - 2, currentPage - 2))]
            }
            // When the current page is more than five pages away from both the beginning and the end, two ellipses are used.
            return [...numList.slice(0, 3), ellipsis, ...numList.slice(currentPage - 2, currentPage + 1), ellipsis, ...numList.slice(-2)];
        }

        const pageList = formatPageList();

        // Single Page Handling (No Pagination) - don't render pagination if there is no need for it.
        if (numPages === 1) {
            return <></>;
        }

        return (
            <div className="m-pagination-row l-mt-xs">
                {/* Arrow left - should be turned off if we're at the leftmost page. */}
                {currentPage > 1 ? (
                    <a href="#/" className="m-pagination-row__page" onClick={() => onPageChange(currentPage - 1)}>❮</a>
                ) : (
                    <span className="m-pagination-row__page m-pagination-row__page--disabled">❮</span>
                )}

                {pageList.map((page, index) => {
                    // If we're looking at an "ellipsis", generate an ellipsis.
                    if (page === ellipsis) {
                        return (
                            <span key={`paginationItem${index}`} className="m-pagination-row__page m-pagination-row__page--disabled">...</span>
                        )
                    }

                    // Otherwise, let's generate a regular item.
                    return (
                        <a 
                            key={`paginationItem${index}`}
                            href="#/" 
                            className={
                            `m-pagination-row__page ${
                                page === currentPage 
                                ? "m-pagination-row__page--current" 
                                : "m-pagination-row__page--unselected"
                            }`} 
                            onClick={() => onPageChange(page)}
                        >
                            {page}
                        </a>
                    );
                })}

                {/* Arrow right - should be turned off if we're at the rightmost page. */}
                {currentPage < numPages ? (
                    <a href="#/" className="m-pagination-row__page" onClick={() => onPageChange(currentPage + 1)}>❯</a>
                ) : (
                    <span className="m-pagination-row__page m-pagination-row__page--disabled">❯</span>
                )}
            </div>
        );
    }
}

export default Pagination;